import Box from "@mui/material/Box";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../AuthContext";
import CodeLogin from "../Components/CodeLogin";
import { Header } from "../Components/Header";
import { ItsMeLogo } from "../Components/ItsMeLogo";
import { LanguageSelector } from "../Components/LanguageSelector";
import LoadingScreen from "../Components/LoadingScreen";
import { OpenSurveys } from "../Components/OpenSurveys";
import { ScheduledSurveys } from "../Components/ScheduledSurveys";
import { SurveyType } from "../Components/SurveyType";
import EDPBLogo from "../Images/ClbEDPBLogo.png";
import ContactService from "../Services/ContactService";

export default function Home() {
  const auth = useAuth();
  const { t, i18n } = useTranslation("home");

  const contactService = useMemo(() => new ContactService(), []);

  const [searchParams] = useSearchParams();

  const [surveyTypeId] = useState<string>(searchParams.get("t") || "");
  const [accountId] = useState<string>(searchParams.get("a") || "");

  useEffect(() => {
    if (
      auth.isActive &&
      !auth.isLoading &&
      !auth.isAuthenticated &&
      surveyTypeId.length > 0 &&
      accountId.length > 0
    ) {
      sessionStorage.setItem("AuthCurrentLocation", window.location.href);
    }
  }, [
    auth.isActive,
    auth.isLoading,
    auth.isAuthenticated,
    surveyTypeId.length,
    accountId.length,
  ]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      const insz = auth.user.find(
        (r) => r.type === "http://itsme.services/v2/claim/BENationalNumber"
      );
      if (insz) {
        const email = auth.user.find(
          (r) =>
            r.type ===
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
        )?.value;
        const phoneNumber = auth.user.find(
          (r) => r.type === "phone_number"
        )?.value;
        const lastName = auth.user.find(
          (r) =>
            r.type ===
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"
        )?.value;
        const firstName = auth.user.find(
          (r) =>
            r.type ===
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
        )?.value;
        const gender = auth.user.find(
          (r) =>
            r.type ===
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/gender"
        )?.value;
        const birthDate = auth.user.find(
          (r) =>
            r.type ===
            "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/dateofbirth"
        )?.value;

        if (
          email ||
          phoneNumber ||
          lastName ||
          firstName ||
          gender ||
          birthDate
        ) {
          contactService.upsertContact({
            insz: insz.value,
            mobilePhone: phoneNumber,
            emailAddress: email,
            firstName: firstName,
            lastName: lastName,
            gender: gender,
            birthDate: birthDate,
          });
        }
      }
    }
  }, [auth.user]);

  useEffect(() => {
    const prevLocation = sessionStorage.getItem("AuthCurrentLocation");
    if (prevLocation) {
      sessionStorage.removeItem("AuthCurrentLocation");
      if (window.location.href !== prevLocation) {
        window.location.href = prevLocation;
      }
    }
  }, []);

  if (auth.isLoading && auth.isActive) {
    return (
      <>
        <Header description={t("Vragenlijsten")} />
        <img src={EDPBLogo} alt="CLB EDPB" className="p-3" />
        <Box p={2}>
          <LoadingScreen />
        </Box>
      </>
    );
  }

  if (
    auth.isAuthenticated &&
    surveyTypeId?.length > 0 &&
    accountId?.length > 0
  ) {
    return (
      <>
        <Header description={t("Vragenlijst")} />
        <img src={EDPBLogo} alt="CLB EDPB" className="p-3" />
        <Box p={2}>
          <SurveyType
            surveyTypeId={surveyTypeId}
            accountId={accountId}
            contactSsin={
              auth.user.find(
                (r) =>
                  r.type ===
                    "http://itsme.services/v2/claim/BENationalNumber" ||
                  r.type === "localrrn"
              )?.value
            }
          />
        </Box>
      </>
    );
  }

  if (!auth.isActive) {
    return (
      <>
        <Header description={t("Vragenlijsten")} />
        <img src={EDPBLogo} alt="CLB EDPB" className="p-3" />
        <Box p={2}></Box>
      </>
    );
  }

  if (auth.isAuthenticated) {
    return (
      <>
        <Header description={t("Vragenlijsten")} />
        <img src={EDPBLogo} alt="CLB EDPB" className="p-3" />
        <Box p={2}>
          <h5>{t("Selecteer een taal")}</h5>
          <div className="pb-4">
            <LanguageSelector useshortCodes={false}></LanguageSelector>
          </div>
          <div className="d-flex justify-content-center flex-wrap">
            <ScheduledSurveys
              contactSsin={
                auth.user.find(
                  (r) =>
                    r.type ===
                      "http://itsme.services/v2/claim/BENationalNumber" ||
                    r.type === "localrrn"
                )?.value
              }
            />
            <OpenSurveys
              contactSsin={
                auth.user.find(
                  (r) =>
                    r.type ===
                      "http://itsme.services/v2/claim/BENationalNumber" ||
                    r.type === "localrrn"
                )?.value
              }
            />
          </div>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header description={t("Vragenlijsten")} />
      <img src={EDPBLogo} alt="CLB EDPB" className="p-3" />
      <Box p={2}>
        <Link to="/login">
          <ItsMeLogo
            label={
              i18n.language === "nl"
                ? "Inloggen met itsme"
                : i18n.language === "fr"
                ? "S’identifier avec itsme"
                : "Log in with itsme"
            }
          />
        </Link>
      </Box>
      <Box p={2}>of</Box>
      <CodeLogin />
    </>
  );
}
